import React, { useEffect, useRef, useState } from "react";
import {
  Fire, Barbell, MoonStars, Scales, Drop, Footprints, Camera,
  CheckCircle, ArrowLeft, ArrowRight, Confetti, Trophy, Sparkle, X, Lightning, CalendarBlank,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { canonicalRole } from "@/lib/roles";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fullUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

const WORKOUT_TYPES = ["Strength", "Cardio", "Skills", "Mobility", "Sport"];
const DURATIONS = [30, 45, 60, 90];
const SLEEP_OPTS = [5, 6, 7, 8, 9];
const MOOD_LABELS = ["Rough", "Low", "OK", "Good", "Great"];
const ENERGY_LABELS = ["Drained", "Low", "Steady", "Fresh", "Charged"];
const SORE_LABELS = ["None", "Mild", "Some", "Sore", "Wrecked"];
const RPE_LABELS = ["Easy", "Light", "Moderate", "Hard", "Max"];

// --- small building blocks -----------------------------------------------
const ScalePicker = ({ value, onChange, labels, good = "high", testid }) => (
  <div data-testid={testid}>
    <div className="grid grid-cols-5 gap-2">
      {[1, 2, 3, 4, 5].map((n) => {
        const active = value === n;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            data-testid={`${testid}-${n}`}
            className={`aspect-square rounded-lg border text-lg font-heading tabular-nums transition-all ${
              active
                ? "border-brand-accent bg-brand-accent text-black scale-105 shadow-lg shadow-brand-accent/20"
                : "border-white/10 bg-white/5 text-text-secondary hover:border-white/30"
            }`}
          >
            {n}
          </button>
        );
      })}
    </div>
    <div className="text-center mt-2 text-sm text-brand-accent min-h-[20px] label-tiny">
      {value ? labels[value - 1] : ""}
    </div>
  </div>
);

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`px-4 py-2 rounded-full border text-sm transition-all ${
      active
        ? "border-brand-accent bg-brand-accent text-black font-medium"
        : "border-white/10 bg-white/5 text-text-secondary hover:border-white/30"
    }`}
  >
    {children}
  </button>
);

const Field = ({ label, icon: Icon, children }) => (
  <div>
    <div className="label-tiny text-text-secondary mb-2 flex items-center gap-1.5">
      {Icon && <Icon size={13} />} {label}
    </div>
    {children}
  </div>
);

// --- calendar heatmap ----------------------------------------------------
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const parseD = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(Date.UTC(y, m - 1, d)); };
const fmtD = (dt) => dt.toISOString().slice(0, 10);

const CheckinHeatmap = ({ history }) => {
  const scrollRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
  }, [history]);
  if (!history) return <div className="text-text-secondary text-sm py-10 text-center">Loading…</div>;
  const logged = new Set(history.logged_dates || []);
  const trained = new Set(history.trained_dates || []);
  const from = parseD(history.from);
  const to = parseD(history.to);
  const toYmd = history.to;

  // grid starts on the Sunday on/before `from`
  const gridStart = new Date(from);
  gridStart.setUTCDate(from.getUTCDate() - from.getUTCDay());
  const days = [];
  for (let cur = new Date(gridStart); cur <= to; cur.setUTCDate(cur.getUTCDate() + 1)) {
    days.push(fmtD(cur));
  }
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));

  const cellClass = (d) => {
    if (d > toYmd) return "bg-transparent";
    if (logged.has(d) && trained.has(d)) return "bg-brand-accent";
    if (logged.has(d)) return "bg-brand-accent/40";
    if (d < history.from) return "bg-white/[0.03]";
    return "bg-white/[0.07]";
  };

  // month labels: show above the first week whose month differs from the prior
  let lastMonth = -1;
  const monthLabels = weeks.map((w) => {
    const first = parseD(w[0]);
    const mo = first.getUTCMonth();
    if (mo !== lastMonth) { lastMonth = mo; return MONTHS[mo]; }
    return "";
  });

  const Stat = ({ value, label, testid }) => (
    <div className="border border-white/10 rounded-lg p-3 text-center">
      <div className="font-heading text-2xl tabular-nums" data-testid={testid}>{value}</div>
      <div className="label-tiny text-[10px] text-text-secondary mt-1">{label}</div>
    </div>
  );

  return (
    <div data-testid="checkin-heatmap">
      <div className="grid grid-cols-4 gap-2 mb-5">
        <Stat value={history.streak} label="STREAK" testid="heatmap-streak" />
        <Stat value={history.longest} label="BEST" testid="heatmap-longest" />
        <Stat value={history.this_month} label="THIS MONTH" testid="heatmap-month" />
        <Stat value={history.total_logged} label="TOTAL" testid="heatmap-total" />
      </div>

      <div ref={scrollRef} className="overflow-x-auto pb-2">
        <div className="inline-flex flex-col gap-1 min-w-max">
          <div className="flex gap-1 pl-7">
            {monthLabels.map((m, i) => (
              <div key={i} className="w-3.5 text-[9px] text-text-secondary">{m}</div>
            ))}
          </div>
          <div className="flex">
            <div className="flex flex-col gap-1 pr-1 justify-between text-[9px] text-text-secondary">
              {["", "Mon", "", "Wed", "", "Fri", ""].map((d, i) => (
                <div key={i} className="h-3.5 leading-3.5">{d}</div>
              ))}
            </div>
            <div className="flex gap-1">
              {weeks.map((w, wi) => (
                <div key={wi} className="flex flex-col gap-1">
                  {Array.from({ length: 7 }).map((_, di) => {
                    const d = w[di];
                    return (
                      <div
                        key={di}
                        title={d || ""}
                        className={`w-3.5 h-3.5 rounded-sm ${d ? cellClass(d) : "bg-transparent"}`}
                        data-testid={d === toYmd ? "heatmap-today-cell" : undefined}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4 mt-4 text-[10px] text-text-secondary label-tiny">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-brand-accent" /> TRAINED</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-brand-accent/40" /> REST-DAY LOG</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-white/[0.07]" /> MISSED</span>
      </div>
    </div>
  );
};

// --- main component ------------------------------------------------------
export default function DailyCheckIn() {
  const { user } = useAuth();
  const role = canonicalRole(user?.primary_role);
  const inAudience = role === "athlete" || role === "general_client";

  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null); // post response when finished
  const [uploading, setUploading] = useState(false);
  const autoPopped = useRef(false);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    trained: null, workout_type: null, workout_duration_min: null, intensity: null,
    mood: null, energy: null, soreness: null, sleep_hours: null,
    weight_kg: null, steps: null, hydration_ml: null, note: "", photo_url: null,
  });
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const load = () => {
    api.get("/daily/checkin").then(({ data }) => {
      setStatus(data);
      if (data.today) {
        const t = data.today;
        setForm((f) => ({
          ...f,
          trained: t.trained ?? null, workout_type: t.workout_type ?? null,
          workout_duration_min: t.workout_duration_min ?? null, intensity: t.intensity ?? null,
          mood: t.mood ?? null, energy: t.energy ?? null, soreness: t.soreness ?? null,
          sleep_hours: t.sleep_hours ?? null, weight_kg: t.weight_kg ?? null,
          steps: t.steps ?? null, hydration_ml: t.hydration_ml ?? null,
          note: t.note ?? "", photo_url: t.photo_url ?? null,
        }));
      }
    }).catch(() => {});
  };

  useEffect(() => { if (inAudience) load(); }, [inAudience]);

  // Pop the check-in open when the user opens the app, if not done today.
  // Politely skip if another modal (onboarding/tour) is already open.
  useEffect(() => {
    if (!status || autoPopped.current) return;
    if (!status.done_today) {
      const t = setTimeout(() => {
        const otherModal = typeof document !== "undefined" && document.querySelector('[role="dialog"]');
        if (otherModal) return;
        autoPopped.current = true;
        setStep(0); setResult(null); setOpen(true);
      }, 900);
      return () => clearTimeout(t);
    }
  }, [status]);

  if (!inAudience) return null;

  const STEPS = [
    { key: "train", title: "Did you train today?", desc: "A quick yes keeps your streak alive." },
    { key: "feel", title: "How do you feel?", desc: "Tap what matches your body today." },
    { key: "body", title: "Sleep & body", desc: "Optional — log what you know." },
    { key: "note", title: "Anything to remember?", desc: "A line or a photo of the day." },
  ];

  const openFlow = () => { setStep(0); setResult(null); setOpen(true); };

  const openHistory = () => {
    setHistory(null);
    setHistoryOpen(true);
    api.get("/daily/checkin/history?days=182").then(({ data }) => setHistory(data)).catch(() => toast.error("Could not load history"));
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v !== null && v !== "" && v !== undefined) payload[k] = v;
      });
      const { data } = await api.post("/daily/checkin", payload);
      setResult(data);
      setStatus((s) => ({ ...(s || {}), done_today: true, streak: data.streak, longest: data.longest, week: data.week }));
      setStep(STEPS.length); // completion screen
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save check-in");
    } finally {
      setSaving(false);
    }
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/daily/checkin/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set({ photo_url: data.url });
      toast.success("Photo added");
    } catch {
      toast.error("Photo upload failed");
    } finally {
      setUploading(false);
    }
  };

  const streak = status?.streak ?? 0;
  const week = status?.week;
  const done = status?.done_today;

  // ---- The reminder / streak card shown on the hub ----
  const card = (
    <div
      className="tactical-card p-5 mb-6 relative overflow-hidden animate-fade-up"
      data-testid="daily-checkin-card"
    >
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 ${streak > 0 ? "bg-orange-500/15" : "bg-white/5"}`}>
            <Fire size={30} weight={streak > 0 ? "fill" : "duotone"} className={streak > 0 ? "text-orange-400" : "text-text-secondary"} />
          </div>
          <div>
            <div className="label-tiny text-brand-accent mb-1">// DAILY CHECK-IN</div>
            <div className="font-heading text-2xl leading-none" data-testid="daily-checkin-streak">
              {streak > 0 ? `${streak}-DAY STREAK` : "START YOUR STREAK"}
            </div>
            <div className="text-xs text-text-secondary mt-1">
              {done ? "Logged for today — nice work." : "Takes under a minute. Keep the chain going."}
            </div>
          </div>
        </div>
        <button
          onClick={openFlow}
          data-testid="daily-checkin-open"
          className={`px-5 py-2.5 text-xs label-tiny tracking-widest flex items-center gap-2 rounded ${
            done ? "border border-white/15 hover:border-brand-accent" : "btn-kinetic"
          }`}
        >
          {done ? <><CheckCircle size={14} weight="fill" className="text-green-400" /> VIEW / EDIT</> : <><Lightning size={14} weight="fill" /> LOG TODAY</>}
        </button>
      </div>

      {week?.week_days && (
        <div className="flex items-center gap-1.5 mt-4" data-testid="daily-checkin-week">
          {week.week_days.map((d) => (
            <div
              key={d.date}
              title={d.date}
              className={`h-2 flex-1 rounded-full ${d.done ? "bg-brand-accent" : "bg-white/10"}`}
            />
          ))}
        </div>
      )}

      <button
        onClick={openHistory}
        data-testid="daily-checkin-history-open"
        className="mt-3 text-xs label-tiny text-text-secondary hover:text-brand-accent flex items-center gap-1.5"
      >
        <CalendarBlank size={13} /> VIEW CHECK-IN HISTORY
      </button>
    </div>
  );

  const stepKey = STEPS[step]?.key;
  const isCompletion = step >= STEPS.length;

  return (
    <>
      {card}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-surface border-white/10" data-testid="daily-checkin-dialog">
          {!isCompletion ? (
            <>
              <DialogHeader>
                <div className="flex items-center gap-1.5 mb-1">
                  {STEPS.map((_, i) => (
                    <div key={i} className={`h-1 flex-1 rounded-full ${i <= step ? "bg-brand-accent" : "bg-white/10"}`} />
                  ))}
                </div>
                <DialogTitle className="font-heading text-xl">{STEPS[step].title}</DialogTitle>
                <DialogDescription className="text-text-secondary text-sm">{STEPS[step].desc}</DialogDescription>
              </DialogHeader>

              <div className="py-2 min-h-[220px]">
                {stepKey === "train" && (
                  <div className="space-y-5">
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => set({ trained: true })}
                        data-testid="checkin-trained-yes"
                        className={`py-6 rounded-xl border flex flex-col items-center gap-2 transition-all ${form.trained === true ? "border-brand-accent bg-brand-accent/10" : "border-white/10 bg-white/5 hover:border-white/30"}`}
                      >
                        <Barbell size={28} weight="duotone" className="text-brand-accent" />
                        <span className="label-tiny">YES, I TRAINED</span>
                      </button>
                      <button
                        onClick={() => set({ trained: false, workout_type: null, workout_duration_min: null, intensity: null })}
                        data-testid="checkin-trained-no"
                        className={`py-6 rounded-xl border flex flex-col items-center gap-2 transition-all ${form.trained === false ? "border-brand-accent bg-brand-accent/10" : "border-white/10 bg-white/5 hover:border-white/30"}`}
                      >
                        <MoonStars size={28} weight="duotone" className="text-text-secondary" />
                        <span className="label-tiny">REST DAY</span>
                      </button>
                    </div>
                    {form.trained === true && (
                      <div className="space-y-4 animate-fade-up">
                        <Field label="TYPE">
                          <div className="flex flex-wrap gap-2">
                            {WORKOUT_TYPES.map((t) => (
                              <Chip key={t} active={form.workout_type === t} onClick={() => set({ workout_type: t })} testid={`checkin-type-${t}`}>{t}</Chip>
                            ))}
                          </div>
                        </Field>
                        <Field label="DURATION (MIN)">
                          <div className="flex flex-wrap gap-2">
                            {DURATIONS.map((m) => (
                              <Chip key={m} active={form.workout_duration_min === m} onClick={() => set({ workout_duration_min: m })} testid={`checkin-dur-${m}`}>{m}</Chip>
                            ))}
                          </div>
                        </Field>
                        <Field label="HOW HARD DID IT FEEL?">
                          <ScalePicker value={form.intensity} onChange={(n) => set({ intensity: n })} labels={RPE_LABELS} testid="checkin-intensity" />
                        </Field>
                      </div>
                    )}
                  </div>
                )}

                {stepKey === "feel" && (
                  <div className="space-y-5">
                    <Field label="MOOD" icon={Sparkle}>
                      <ScalePicker value={form.mood} onChange={(n) => set({ mood: n })} labels={MOOD_LABELS} testid="checkin-mood" />
                    </Field>
                    <Field label="ENERGY" icon={Lightning}>
                      <ScalePicker value={form.energy} onChange={(n) => set({ energy: n })} labels={ENERGY_LABELS} testid="checkin-energy" />
                    </Field>
                    <Field label="SORENESS" icon={Fire}>
                      <ScalePicker value={form.soreness} onChange={(n) => set({ soreness: n })} labels={SORE_LABELS} testid="checkin-soreness" />
                    </Field>
                  </div>
                )}

                {stepKey === "body" && (
                  <div className="space-y-5">
                    <Field label="SLEEP (HOURS)" icon={MoonStars}>
                      <div className="flex flex-wrap gap-2 items-center">
                        {SLEEP_OPTS.map((h) => (
                          <Chip key={h} active={Number(form.sleep_hours) === h} onClick={() => set({ sleep_hours: h })} testid={`checkin-sleep-${h}`}>{h}h</Chip>
                        ))}
                        <Input
                          type="number" step="0.5" placeholder="Other"
                          value={SLEEP_OPTS.includes(Number(form.sleep_hours)) ? "" : (form.sleep_hours ?? "")}
                          onChange={(e) => set({ sleep_hours: e.target.value === "" ? null : parseFloat(e.target.value) })}
                          className="w-20" data-testid="checkin-sleep-input"
                        />
                      </div>
                    </Field>
                    <div className="grid grid-cols-3 gap-3">
                      <Field label="WEIGHT (KG)" icon={Scales}>
                        <Input type="number" step="0.1" value={form.weight_kg ?? ""} onChange={(e) => set({ weight_kg: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid="checkin-weight" />
                      </Field>
                      <Field label="STEPS" icon={Footprints}>
                        <Input type="number" value={form.steps ?? ""} onChange={(e) => set({ steps: e.target.value === "" ? null : parseInt(e.target.value) })} data-testid="checkin-steps" />
                      </Field>
                      <Field label="WATER (ML)" icon={Drop}>
                        <Input type="number" value={form.hydration_ml ?? ""} onChange={(e) => set({ hydration_ml: e.target.value === "" ? null : parseInt(e.target.value) })} data-testid="checkin-hydration" />
                      </Field>
                    </div>
                  </div>
                )}

                {stepKey === "note" && (
                  <div className="space-y-4">
                    <Field label="NOTE">
                      <Textarea rows={3} value={form.note} onChange={(e) => set({ note: e.target.value })} placeholder="One win, one struggle, or how the day went…" data-testid="checkin-note" />
                    </Field>
                    <Field label="PHOTO (OPTIONAL)" icon={Camera}>
                      <input ref={fileRef} type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="checkin-photo-input" />
                      {form.photo_url ? (
                        <div className="relative inline-block">
                          <img src={fullUrl(form.photo_url)} alt="day" className="h-28 rounded-lg object-cover border border-white/10" />
                          <button onClick={() => set({ photo_url: null })} className="absolute -top-2 -right-2 bg-surface border border-white/20 rounded-full p-1" data-testid="checkin-photo-remove"><X size={12} /></button>
                        </div>
                      ) : (
                        <button onClick={() => fileRef.current?.click()} disabled={uploading} className="border border-dashed border-white/20 rounded-lg px-4 py-6 w-full flex flex-col items-center gap-2 text-text-secondary hover:border-brand-accent transition" data-testid="checkin-photo-add">
                          <Camera size={22} />
                          <span className="label-tiny">{uploading ? "UPLOADING…" : "ADD A PHOTO"}</span>
                        </button>
                      )}
                    </Field>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between gap-2 pt-2">
                <button
                  onClick={() => (step === 0 ? setOpen(false) : setStep(step - 1))}
                  className="px-3 py-2 text-xs label-tiny text-text-secondary hover:text-white flex items-center gap-1"
                  data-testid="checkin-back"
                >
                  <ArrowLeft size={14} /> {step === 0 ? "CLOSE" : "BACK"}
                </button>
                {step < STEPS.length - 1 ? (
                  <button onClick={() => setStep(step + 1)} className="btn-kinetic px-5 py-2.5 text-xs label-tiny tracking-widest flex items-center gap-2" data-testid="checkin-next">
                    NEXT <ArrowRight size={14} />
                  </button>
                ) : (
                  <button onClick={submit} disabled={saving} className="btn-kinetic px-5 py-2.5 text-xs label-tiny tracking-widest flex items-center gap-2 disabled:opacity-50" data-testid="checkin-finish">
                    <CheckCircle size={14} weight="fill" /> {saving ? "SAVING…" : "FINISH CHECK-IN"}
                  </button>
                )}
              </div>
            </>
          ) : (
            // ---------------- Completion / celebration ----------------
            <div className="text-center py-4" data-testid="checkin-complete">
              <DialogHeader>
                <DialogTitle className="sr-only">Check-in complete</DialogTitle>
                <DialogDescription className="sr-only">Your daily check-in is saved.</DialogDescription>
              </DialogHeader>
              <div className="w-20 h-20 mx-auto rounded-full bg-orange-500/15 flex items-center justify-center mb-4 animate-fade-up">
                <Fire size={44} weight="fill" className="text-orange-400" />
              </div>
              <div className="font-heading text-4xl" data-testid="checkin-complete-streak">{result?.streak ?? 0}</div>
              <div className="label-tiny text-brand-accent mt-1">DAY STREAK</div>

              {result?.milestone ? (
                <div className="mt-4 mx-auto inline-flex items-center gap-2 bg-brand-accent/15 border border-brand-accent/40 text-brand-accent px-4 py-2 rounded-full" data-testid="checkin-milestone">
                  <Trophy size={16} weight="fill" /> {result.milestone}-day milestone! Keep it rolling.
                </div>
              ) : (
                <p className="text-sm text-text-secondary mt-3">Logged for today. See you tomorrow.</p>
              )}

              {result?.week && (
                <div className="grid grid-cols-3 gap-2 mt-6 text-left" data-testid="checkin-complete-recap">
                  <div className="border border-white/10 rounded-lg p-3">
                    <div className="font-heading text-2xl tabular-nums">{result.week.days_logged}/7</div>
                    <div className="label-tiny text-text-secondary mt-1">THIS WEEK</div>
                  </div>
                  <div className="border border-white/10 rounded-lg p-3">
                    <div className="font-heading text-2xl tabular-nums">{result.week.sessions_trained}</div>
                    <div className="label-tiny text-text-secondary mt-1">SESSIONS</div>
                  </div>
                  <div className="border border-white/10 rounded-lg p-3">
                    <div className="font-heading text-2xl tabular-nums">{result.week.avg_sleep ?? "—"}</div>
                    <div className="label-tiny text-text-secondary mt-1">AVG SLEEP</div>
                  </div>
                </div>
              )}

              <button onClick={() => setOpen(false)} className="btn-kinetic px-6 py-2.5 text-xs label-tiny tracking-widest mt-6 inline-flex items-center gap-2" data-testid="checkin-done">
                <Confetti size={14} weight="fill" /> DONE
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-lg bg-surface border-white/10" data-testid="checkin-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2"><CalendarBlank size={20} /> Check-in history</DialogTitle>
            <DialogDescription className="text-text-secondary text-sm">Every day you log keeps the chain growing.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <CheckinHeatmap history={history} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
